import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  doc, 
  updateDoc, 
  Timestamp,
  orderBy,
  limit as firestoreLimit
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { DRXError } from '@/lib/drx-error';

export interface SupportMessage {
  id?: string;
  name: string;
  email: string;
  phone?: string;
  subject: string;
  message: string;
  category: 'general' | 'technical' | 'billing' | 'course' | 'connect' | 'other';
  status: 'new' | 'inProgress' | 'resolved' | 'closed';
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  assignedTo?: string;
  notes?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  resolvedAt?: Timestamp;
}

export class SupportMessageRepository {
  private collectionRef = collection(db, 'supportMessages');

  async create(data: Omit<SupportMessage, 'id' | 'createdAt' | 'updatedAt' | 'status'>): Promise<string> {
    try {
      const messageData: Omit<SupportMessage, 'id'> = {
        ...data,
        status: 'new',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const docRef = await addDoc(this.collectionRef, messageData);
      return docRef.id;
    } catch (error) {
      throw new DRXError({
        code: 'SUPPORT_MESSAGE_CREATE_FAILED',
        devMessage: `Failed to create support message for ${data.email}`,
        userMessage: 'Could not send your message. Please try again.',
        cause: error,
        level: 'error',
      });
    }
  }

  async getAll(options?: {
    status?: string;
    category?: string;
    limit?: number;
  }): Promise<SupportMessage[]> {
    try {
      let q = query(this.collectionRef, orderBy('createdAt', 'desc'));

      if (options?.status) {
        q = query(q, where('status', '==', options.status));
      }

      if (options?.category) {
        q = query(q, where('category', '==', options.category));
      }

      if (options?.limit) {
        q = query(q, firestoreLimit(options.limit));
      }

      const snapshot = await getDocs(q);
      const messages: SupportMessage[] = [];

      snapshot.forEach((doc) => {
        messages.push({
          id: doc.id,
          ...doc.data(),
        } as SupportMessage);
      });

      return messages;
    } catch (error) {
      throw new DRXError({
        code: 'SUPPORT_MESSAGE_FETCH_FAILED',
        devMessage: `Failed to fetch support messages with filters ${JSON.stringify(options ?? {})}`,
        userMessage: 'Could not load support messages.',
        cause: error,
        level: 'error',
      });
    }
  }

  async updateStatus(
    messageId: string, 
    status: SupportMessage['status'], 
    notes?: string
  ): Promise<void> {
    try {
      const docRef = doc(db, 'supportMessages', messageId);
      const updateData: any = {
        status,
        updatedAt: Timestamp.now(),
      };

      if (notes) {
        updateData.notes = notes;
      }

      if (status === 'resolved' || status === 'closed') {
        updateData.resolvedAt = Timestamp.now();
      }

      await updateDoc(docRef, updateData);
    } catch (error) {
      throw new DRXError({
        code: 'SUPPORT_MESSAGE_STATUS_UPDATE_FAILED',
        devMessage: `Failed to update support message ${messageId} to status ${status}`,
        userMessage: 'Could not update support message status.',
        cause: error,
        level: 'error',
      });
    }
  }

  async updatePriority(messageId: string, priority: SupportMessage['priority']): Promise<void> {
    try {
      const docRef = doc(db, 'supportMessages', messageId);
      await updateDoc(docRef, {
        priority,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      throw new DRXError({
        code: 'SUPPORT_MESSAGE_PRIORITY_UPDATE_FAILED',
        devMessage: `Failed to update support message ${messageId} priority to ${priority ?? 'unset'}`,
        userMessage: 'Could not update support message priority.',
        cause: error,
        level: 'error',
      });
    }
  }

  async assignTo(messageId: string, userId: string): Promise<void> {
    try {
      const docRef = doc(db, 'supportMessages', messageId);
      await updateDoc(docRef, {
        assignedTo: userId,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      throw new DRXError({
        code: 'SUPPORT_MESSAGE_ASSIGN_FAILED',
        devMessage: `Failed to assign support message ${messageId} to user ${userId}`,
        userMessage: 'Could not assign support message.',
        cause: error,
        level: 'error',
      });
    }
  }
}
